import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, Loader2, Mail, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/admin";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            setError("Enter your email and password.");
            return;
        }
        setLoading(true);
        setError("");
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome back.");
            // Employees land on /staff, admins on /admin (or wherever they were headed)
            const target = res.user?.role === "employee" ? "/staff" : from;
            navigate(target, { replace: true });
        } else {
            setError(res.error || "Login failed.");
        }
    };

    return (
        <div className="min-h-screen bg-[#F7F5F0] grid lg:grid-cols-2" data-testid="admin-login-page">
            {/* Visual side */}
            <div className="hidden lg:block relative bg-[#1A2230] overflow-hidden">
                <div className="absolute inset-0 grain opacity-90" style={{
                    backgroundImage: "linear-gradient(135deg, rgba(45,74,62,0.95), rgba(26,34,30,0.95))"
                }} />
                <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
                    <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-[14px]" data-testid="login-back-home">
                        <ArrowLeft className="h-4 w-4" /> Back to website
                    </Link>
                    <div>
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium mb-3">
                            Crossroads to Change Residential Treatment Center · Admin
                        </p>
                        <h1 className="font-[Outfit] text-4xl xl:text-5xl font-semibold tracking-tight leading-tight">
                            A quiet place to look after the work that matters.
                        </h1>
                        <p className="mt-5 text-[15px] text-white/70 max-w-md leading-relaxed">
                            Review referrals, respond to inquiries, and keep
                            the day's work moving — all in one place.
                        </p>
                    </div>
                    <p className="text-[12px] text-white/50">
                        © {new Date().getFullYear()} Crossroads to Change Residential Treatment Center · Tomball, TX
                    </p>
                </div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <div className="flex items-center gap-2 mb-8 lg:hidden">
                        <Link to="/" className="text-[#6F6B63] hover:text-[#0E2A47] inline-flex items-center gap-2 text-[14px]">
                            <ArrowLeft className="h-4 w-4" /> Back to website
                        </Link>
                    </div>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="h-11 w-11 rounded-full bg-[#EFEBE0] grid place-items-center text-[#0E2A47]">
                            <Lock className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                                Admin login
                            </p>
                            <h2 className="font-[Outfit] text-2xl font-semibold tracking-tight">
                                Sign in
                            </h2>
                        </div>
                    </div>

                    <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                        <div>
                            <label className="rtc-label">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F8B83]" />
                                <input
                                    type="email"
                                    required
                                    autoComplete="username"
                                    className="rtc-input pl-10"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="login-email"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="rtc-label">Password</label>
                            <div className="relative">
                                <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F8B83]" />
                                <input
                                    type={showPwd ? "text" : "password"}
                                    required
                                    autoComplete="current-password"
                                    className="rtc-input pl-10 pr-16"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((s) => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-[#6F6B63] hover:text-[#0E2A47] px-2 py-1"
                                    data-testid="login-toggle-password"
                                >
                                    {showPwd ? "Hide" : "Show"}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="rounded-xl bg-[#fdf6e8] border border-[#C8A24A]/30 text-[#8a6a1f] px-4 py-3 text-[13.5px]" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0E2A47] text-white px-7 py-3.5 text-[14.5px] font-medium hover:bg-[#08182B] disabled:opacity-50 transition-colors"
                            data-testid="login-submit"
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                            Sign in
                        </button>

                        <p className="text-[12.5px] text-[#6F6B63] text-center mt-4">
                            Trouble signing in? Contact your administrator.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
